import {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

// Per-exam entitlement. The subscriptions/{uid} doc holds the reusable Stripe
// customer id plus an `exams` map (examId -> { status, ... }). Access is granted
// per exam: isEntitled(examId) is true only when that exam's status is 'active'.
// This is a live, cosmetic mirror — the real gate is server-side.
const Ctx = createContext({
  entitlements: {},
  isEntitled: () => false,
  hasBilling: false,
  loading: true,
});

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [entitlements, setEntitlements] = useState({});
  const [hasBilling, setHasBilling] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setEntitlements({});
      setHasBilling(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      doc(db, 'subscriptions', user.uid),
      (snap) => {
        const data = snap.data();
        setEntitlements(data?.exams ?? {});
        setHasBilling(Boolean(data?.stripeCustomerId));
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [user]);

  const isEntitled = (examId) => entitlements?.[examId]?.status === 'active';

  return (
    <Ctx.Provider value={{ entitlements, isEntitled, hasBilling, loading }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSubscription() {
  return useContext(Ctx);
}
